import { useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  Button,
  Grid,
  InputLabel,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { LoadingButton, MainCard } from '@weberlo/components'

import { useFormik } from 'formik'
import { ApiError, WorkspaceService } from 'generated'
import useToast from 'hooks/useToast'
import * as yup from 'yup'

const AddWorkspace = ({ open, close }: Props) => {
  const { toast } = useToast()

  const queryClient = useQueryClient()

  const { mutate, isLoading, error } = useMutation({
    mutationFn: (values: { name: string }) =>
      WorkspaceService.postWorkspace(values),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] })
      toast({
        message: 'Workspace was created',
        alertColor: 'success',
      })
      close()
    },
  })

  useEffect(() => {
    if (error) {
      const issue = error as ApiError

      toast({
        message: issue?.message || 'Creating error',
        alertColor: 'error',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: yup.object({
      name: yup.string().required('Name is required'),
    }),
    onSubmit: async values => {
      await mutate(values)
    },
  })

  return (
    <Modal
      open={open}
      onClose={close}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <MainCard
        sx={{ p: 3 }}
        modal
        darkTitle
        content={false}>
        <Typography
          variant="h3"
          sx={{ mb: 3 }}>
          Create Workspace
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={3}>
            <Grid
              item
              xs={12}>
              <Stack spacing={1}>
                <InputLabel htmlFor="name">*Name</InputLabel>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  placeholder="Enter a name for your workspace"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}>
              <Stack
                direction="row"
                justifyContent="end">
                <Button
                  sx={{ mr: 1 }}
                  size="small"
                  onClick={close}>
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  color="secondary"
                  loading={isLoading}
                  variant="contained">
                  CREATE
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </form>
      </MainCard>
    </Modal>
  )
}

interface Props {
  open: boolean
  close: () => void
}

export default AddWorkspace
